import React, { useState } from 'react'
import { Box, Grid, Typography } from '@material-ui/core'
import type { DialogProps } from '@paintscout/ui'
import {
  useClientOptions,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FollowUpTable,
  useUser,
  Collapse,
  Flex,
  Switch
} from '@paintscout/ui'
import * as Sentry from '@sentry/react'
import { Form, Formik } from 'formik'
import { getFeature, getFollowUps, getObjectLabels, toggleFollowUp } from '@paintscout/util/builder'
import { useSnackbar } from 'notistack'
import type { QuoteDocument } from 'paintscout'
import type { FollowUp } from '@paintscout/api'
import { isQuoteEditable } from '@paintscout/util/util'
import EditableToggle from '../../EditableToggle'
import { useQuote } from '../../context'

export interface EditQuoteFollowUpDialogProps extends DialogProps {
  onConfirm: (updatedQuote: QuoteDocument, isDirty: boolean) => void
  onCancel: () => void
  onToggleEdit: (editStatus: boolean) => void
  permissionToEdit?: boolean
  editable: boolean
}

export interface FollowUpFormValues {
  followUps?: FollowUp[]
  followUpsDisabled?: boolean
}

export default function EditQuoteFollowUpDialog({
  onToggleEdit,
  editable,
  permissionToEdit,
  onConfirm,
  onCancel,
  ...props
}: EditQuoteFollowUpDialogProps) {
  const { preferences, trialExpired } = useUser()
  const { quote } = useQuote()
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const { quote: quoteLabel } = getObjectLabels({ options, quote })
  const followUps = getFollowUps({ preferences, options })
  const hasSettingsCommunications = getFeature({
    options,
    path: 'settingsCommunications.enabled'
  })

  let applicableFollowUps = followUps.order
    .map((key) => followUps.values[key])
    // only show followUps that would apply to this quote
    .filter((followUp) => followUp?.active && !!quote.is_invoice === !!followUp.isInvoice)
    .filter((followUp) => {
      // If follow-up created is older than quote sent date, don't show it unless it is retroactive followup
      if (followUp.created > quote.dates.client.sent && !followUp.retroactive) {
        return false
      }
      return true
    })
    .map((followUp) => ({
      ...followUp,
      // if quote has follow up disabled, mark it as inactive for the FollowUp table
      sendEmail: quote.followUps?.[followUp.key] ? !quote.followUps[followUp.key]?.disabled : followUp.sendEmail,
      sendText: quote.followUps?.[followUp.key]?.sendText ?? followUp.sendText
    }))
  // Additional filter for invoice
  if (!quote.is_invoice) {
    applicableFollowUps = applicableFollowUps.filter(
      (followUp) =>
        followUp.quoteTypes.includes(quote.int_ext.value) ||
        quote.int_ext.value === 'all' ||
        followUp.quoteTypes.includes('__all__')
    )
  }

  const [editToggleAnchor, setEditToggleAnchor] = useState<{
    top: number
    left: number
  }>(null)
  const [isEditing, setIsEditing] = useState(editable)
  const isEditable = permissionToEdit && (isQuoteEditable(quote) || isEditing) && !trialExpired
  const followUpsTitle = `${quote.is_invoice ? 'Invoice' : quoteLabel.value} Follow-Ups`

  return (
    <Dialog open={props.open} onClose={onCancel}>
      <DialogTitle loading={props.loading}>{followUpsTitle}</DialogTitle>
      <Formik
        initialValues={{
          followUps: applicableFollowUps,
          followUpsDisabled: quote.followUpsDisabled
        }}
        onSubmit={async (values) => {
          try {
            let isDirty = quote.followUpsDisabled !== values.followUpsDisabled
            // update toggled followUps
            const updatedQuote: QuoteDocument = (values.followUps ?? []).reduce(
              (quote, followUp) => {
                const existingFollowUp = quote?.followUps?.[followUp.key]
                if (
                  existingFollowUp?.disabled !== !followUp.sendEmail ||
                  existingFollowUp?.sendText !== followUp.sendText
                ) {
                  isDirty = true
                  return toggleFollowUp({
                    quote,
                    key: followUp.key,
                    enabled: followUp.sendEmail,
                    sendText: followUp.sendText
                  })
                } else {
                  return quote
                }
              },
              { ...quote, followUpsDisabled: values.followUpsDisabled }
            )
            onConfirm(updatedQuote, isDirty)
          } catch (error) {
            Sentry.captureException(error, { tags: { quoteId: quote._id } })
            enqueueSnackbar('Unable To Update Follow Ups', { variant: 'error' })
          }
        }}
      >
        {(formik) => (
          <>
            <DialogContent>
              <EditableToggle
                position={editToggleAnchor}
                editable={isEditable}
                disabled={trialExpired || !permissionToEdit}
                onToggle={(ev: boolean) => {
                  setIsEditing(ev)
                  onToggleEdit(ev)
                }}
                onClose={() => setEditToggleAnchor(null)}
              >
                <Form
                  onClick={(ev: React.MouseEvent) => {
                    // only call ev.stopPropagation when we're not editable
                    if (!isEditable) {
                      ev.stopPropagation()
                      setEditToggleAnchor({
                        top: ev.clientY,
                        left: ev.clientX
                      })
                    }
                  }}
                  onSubmit={formik.handleSubmit}
                >
                  <Grid item className={'send-quote-follow-ups'}>
                    <Box mt={2}>
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Typography variant={'h4'}>Follow-Ups</Typography>
                        </Box>
                        <Box>
                          <Switch
                            inputProps={{
                              'data-testid': 'toggle-follow-ups'
                            }}
                            checked={!formik.values.followUpsDisabled}
                            onChange={(_, checked) => {
                              formik.setFieldValue('followUpsDisabled', !checked)
                            }}
                            readOnly={!isEditable}
                          />
                        </Box>
                      </Flex>

                      <Collapse show={!formik.values.followUpsDisabled} scroll>
                        <Box mt={1}>
                          {formik.values.followUps.length > 0 ? (
                            <FollowUpTable
                              quoteView
                              quote={quote}
                              followUps={formik.values.followUps}
                              controls={{ toggle: true }}
                              onAction={(action, followUp) => {
                                const index = formik.values.followUps.findIndex((f) => f.key === followUp.key)
                                if (action === 'toggle') {
                                  formik.setFieldValue(`followUps[${index}].sendEmail`, !followUp.sendEmail)
                                } else if (action === 'toggleSendText') {
                                  formik.setFieldValue(`followUps[${index}].sendText`, !followUp.sendText)
                                }
                              }}
                              disabled={!isEditable}
                            />
                          ) : (
                            <Typography align="center" variant="subtitle1">
                              You can configure Follow-Ups in your {hasSettingsCommunications ? 'Settings' : 'Profile'}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  </Grid>
                </Form>
              </EditableToggle>
            </DialogContent>
            <DialogActions
              leftButton={
                <Button onClick={onCancel} variant="textNoPadding">
                  Cancel
                </Button>
              }
            >
              <Button
                disabled={!isEditable}
                variant={'contained'}
                onClick={() => formik.handleSubmit()}
                loading={formik.isSubmitting}
              >
                Done
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  )
}
